/**
 * AuthLayout — "card" centrée d'une page d'authentification.
 *
 * NB : ce composant n'est PAS un layout au sens React Router. Il rend
 * uniquement le contenu propre à chaque page (titre, sous-titre, carte
 * du formulaire, footer optionnel). Le chrome partagé (fond crème, bulles
 * d'arrière-plan, header logo, footer réassurance) est porté par
 * AuthShellLayout — un layout React Router qui enveloppe toutes les routes
 * auth via <Outlet />. Cette séparation garantit que les bulles persistent
 * entre les navigations /login ↔ /signup ↔ /reset-password ↔ /magic-link.
 *
 * Volontairement aucun lien de navigation interne ERP : un utilisateur non
 * authentifié n'a rien à voir d'autre. La sortie = vitrine publique.
 */

import type { ReactNode } from "react";

interface AuthLayoutProps {
  /** Titre affiché au-dessus du formulaire (ex : "Connexion à votre espace"). */
  title: string;
  /** Sous-titre optionnel sous le titre principal. */
  subtitle?: string;
  /** Contenu du formulaire — typiquement un <form>. */
  children: ReactNode;
  /** Bloc optionnel sous la carte (liens "Pas encore de compte ?", etc.). */
  footer?: ReactNode;
}

export function AuthLayout({ title, subtitle, children, footer }: AuthLayoutProps) {
  return (
    <div className="w-full max-w-md">
      {/* Titres au-dessus de la carte */}
      <div className="mb-6 text-center">
        <h1 className="text-2xl font-semibold tracking-tight text-champagne-900 sm:text-3xl">
          {title}
        </h1>
        {subtitle && <p className="mt-2 text-sm text-champagne-800/80">{subtitle}</p>}
      </div>

      {/* Carte du formulaire — fond blanc cassé, ombre douce, rounded-xl */}
      <div className="rounded-xl border border-champagne-200/70 bg-white/95 p-6 shadow-[0_8px_30px_-10px_rgba(56,42,20,0.18)] backdrop-blur-sm sm:p-8">
        {children}
      </div>

      {/* Bloc footer optionnel (liens secondaires sous la carte) */}
      {footer && <div className="mt-6 text-center text-sm">{footer}</div>}
    </div>
  );
}
